import { all, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import type { ReduxAction } from "ee/constants/ReduxActionConstants";
import { generateReactKey } from "utils/generators";
import { getIsAutoLayout } from "selectors/editorSelectors";
import { getLayoutSystemType } from "selectors/layoutSystemSelectors";
import type { WidgetAddChild } from "actions/pageActions";
import { MAIN_CONTAINER_WIDGET_ID } from "constants/WidgetConstants";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
  WidgetReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import { updateWidgetMetaPropAndEval } from "actions/metaActions";
import { focusWidget } from "actions/widgetActions";
import { flatten } from "lodash";
import type { FlattenedWidgetProps } from "reducers/entityReducers/canvasWidgetsReducer";
import { AnvilReduxActionTypes } from "layoutSystems/anvil/integrations/actions/actionTypes";
// import { isMobileLayout } from "selectors/applicationSelectors";
import { LayoutSystemTypes } from "layoutSystems/types";
import {
  getWidget,
  getWidgetByName,
  getWidgetIdsByTypes,
  getWidgets,
  getWidgetsMeta,
} from "sagas/selectors";
import WidgetFactory from "WidgetProvider/factory";
import AppsmithConsole from "utils/AppsmithConsole";
import {
  FlexLayerAlignment,
  LayoutDirection,
} from "layoutSystems/common/utils/constants";

const WidgetTypes = WidgetFactory.widgetTypes;

export function* showDrawerByNameSaga(
  action: ReduxAction<{ drawerName: string }>,
) {
  const widgets: { [widgetId: string]: FlattenedWidgetProps } =
    yield select(getWidgets);
  const drawer: FlattenedWidgetProps | undefined = Object.values(widgets).find(
    (widget: FlattenedWidgetProps) =>
      widget.widgetName === action.payload.drawerName,
  );
  if (drawer) {
    yield put({
      type: ReduxActionTypes.OPEN_DRAWER,
      payload: { drawerId: drawer.widgetId },
    });
  }
}

export function* showDrawerSaga(action: ReduxAction<{ drawerId: string }>) {
  const { drawerId } = action.payload;
  if (!drawerId) return;

  // 关闭其他抽屉（如果需要排他性展示）
  yield put({
    type: ReduxActionTypes.CLOSE_DRAWER,
    payload: { exclude: drawerId },
  });

  yield put(focusWidget(drawerId));

  // 🔧 与 Modal 保持一致：检查 metaProps.isVisible，只在未显示时才更新
  const metaProps: Record<string, any> = yield select(getWidgetsMeta);

  if (!metaProps[drawerId] || !(metaProps[drawerId] as any).isVisible) {
    // 显示 Drawer
    yield put(updateWidgetMetaPropAndEval(drawerId, "isVisible", true));
  }

  const drawer: FlattenedWidgetProps = yield select(getWidget, drawerId);
  if (drawer) {
    AppsmithConsole.info({
      text: `showDrawer('${drawer.widgetName}') was triggered`,
    });
  }
}

export function* closeDrawerSaga(
  action: ReduxAction<{ drawerName?: string; exclude?: string }>,
) {
  const { drawerName } = action.payload;
  try {
    let widgetIds: string[] = [];
    if (drawerName) {
      const widget: FlattenedWidgetProps | undefined = yield select(
        getWidgetByName,
        drawerName,
      );
      widgetIds = widget ? [widget.widgetId] : [];
    } else {
      const metaProps: Record<string, any> = yield select(getWidgetsMeta);
      const drawerWidgetIds: string[] = yield select(getWidgetIdsByTypes, [
        WidgetFactory.widgetTypes.ANTD_GLOBAL_DRAWER_WIDGET,
      ]);
      drawerWidgetIds.forEach((widgetId: string) => {
        if (metaProps[widgetId] && metaProps[widgetId].isVisible) {
          widgetIds.push(widgetId);
        }
      });
    }

    widgetIds = action.payload.exclude
      ? widgetIds.filter((id: string) => id !== action.payload.exclude)
      : widgetIds;

    if (widgetIds && widgetIds.length) {
      yield all(
        flatten(
          widgetIds.map((id: string) => [
            put(updateWidgetMetaPropAndEval(id, "isVisible", false)),
          ]),
        ),
      );
    }
  } catch (e) {
    // no-op
  }
}

// 🔧 新增：自动显示新创建的 Drawer（与 Modal 的 showIfModalSaga 逻辑一致）
export function* showIfDrawerSaga(
  action: ReduxAction<{ widgetId: string; type: string }>,
) {
  if (action.payload.type === WidgetTypes.ANTD_GLOBAL_DRAWER_WIDGET) {
    yield put({
      type: ReduxActionTypes.OPEN_DRAWER,
      payload: { drawerId: action.payload.widgetId },
    });
  }
}

export function* createDrawerSaga(action: ReduxAction<{ drawerName: string }>) {
  try {
    // const isMobile: boolean = yield select(isMobileLayout);
    const drawerWidgetId = generateReactKey();
    const isAutoLayout: boolean = yield select(getIsAutoLayout);
    const layoutSystemType: LayoutSystemTypes =
      yield select(getLayoutSystemType);
    const newWidget: WidgetAddChild = {
      widgetId: MAIN_CONTAINER_WIDGET_ID,
      widgetName: action.payload.drawerName,
      type: WidgetTypes.ANTD_GLOBAL_DRAWER_WIDGET,
      newWidgetId: drawerWidgetId,
      parentRowSpace: 1,
      parentColumnSpace: 1,
      leftColumn: 0,
      topRow: 0,
      columns: 0,
      rows: 0,
      tabId: "",
      isModule: false,
    };
    if (isAutoLayout) {
      const dropPayload = {
        alignment: FlexLayerAlignment.Center,
        index: 0,
        isNewLayer: true,
        layerIndex: 0,
        rowIndex: 0,
      };
      newWidget.props = {
        alignment: FlexLayerAlignment.Center,
      };

      yield put({
        type: ReduxActionTypes.AUTOLAYOUT_ADD_NEW_WIDGETS,
        payload: {
          dropPayload,
          newWidget,
          parentId: MAIN_CONTAINER_WIDGET_ID,
          direction: LayoutDirection.Vertical,
          addToBottom: true,
        },
      });
    } else if (layoutSystemType === LayoutSystemTypes.ANVIL) {
      //TODO(#30604): Refactor to separate this logic from the anvil layout system
      yield put({
        type: AnvilReduxActionTypes.ANVIL_ADD_NEW_WIDGET,
        payload: {
          highlight: { alignment: "none", canvasId: "0" },
          newWidget: { ...newWidget, detachFromLayout: true },
          dragMeta: {
            draggedWidgetTypes: "WIDGETS",
            draggedOn: "MAIN_CANVAS",
          },
        },
      });
    } else {
      yield put({
        type: WidgetReduxActionTypes.WIDGET_ADD_CHILD,
        payload: newWidget,
      });
    }
  } catch (error) {
    console.error(error);
    yield put({
      type: ReduxActionErrorTypes.CREATE_MODAL_ERROR,
      payload: { error },
    });
  }
}

export default function* drawerSagas() {
  yield all([
    takeLatest(ReduxActionTypes.CREATE_DRAWER_INIT, createDrawerSaga),
    takeEvery(ReduxActionTypes.CLOSE_DRAWER, closeDrawerSaga),
    takeLatest(ReduxActionTypes.OPEN_DRAWER, showDrawerSaga),
    takeLatest(ReduxActionTypes.OPEN_DRAWER_BY_NAME, showDrawerByNameSaga),
    takeLatest(WidgetReduxActionTypes.WIDGET_CHILD_ADDED, showIfDrawerSaga),
  ]);
}
